export enum LogLevel {
	Error = 0,
	Warn = 1,
	Info = 2,
}

export const APP_VERSION_KEY = "app_version";
export const ENVIRONMENT_KEY = "environment";

function isDev(): boolean {
	return process.env.NODE_ENV === "development";
}

function getEnvironment(): string {
	const api_url = process.env.NEXT_PUBLIC_API_URL || process.env.API_URL;
	if (api_url?.includes("zetalabs-ai-staging")) {
		return "staging";
	} else if (api_url?.includes("zetalabs-ai-prod")) {
		return "production";
	} else {
		return "dev";
	}
}

export interface LoggerAdapter {
	setLevel(level: LogLevel): void;
	error(error: unknown, context: Record<string, any>): void;
	warn(message: string, context: Record<string, any>): void;
	info(message: string, context: Record<string, any>, tag?: string): void;
}

export class ConsoleLoggerAdapter implements LoggerAdapter {
	private silent = !isDev();
	private logLevel: LogLevel;

	constructor(logLevel: LogLevel = LogLevel.Info) {
		this.logLevel = logLevel;
	}

	setLevel(level: LogLevel) {
		this.logLevel = level;
	}

	error(error: unknown, context: Record<string, any> = {}) {
		if (this.silent) {
			return;
		}

		console.error(error, context);
	}

	warn(message: string, context: Record<string, any> = {}) {
		if (this.silent) {
			return;
		}

		if (this.logLevel >= LogLevel.Warn) {
			console.warn(message, context);
		}
	}

	info(message: string, context: Record<string, any> = {}, tag?: string) {
		if (this.silent) {
			return;
		}

		if (this.logLevel >= LogLevel.Info) {
			console.info(tag ? `[${tag}]: ` : "[Info]: ", message, context);
		}
	}
}

interface LoggerConfig {
	isDevelopment?: boolean;
	showCallerLocation?: boolean;
	adapter?: new () => LoggerAdapter;
}

export class Logger {
	private config: LoggerConfig;
	private adapter: LoggerAdapter;
	private globalContext = new Map<string, any>();

	constructor(options: LoggerConfig = {}) {
		this.config = {
			isDevelopment: isDev(),
			showCallerLocation: false,
			...options,
		};

		this.adapter =
			this.config.isDevelopment || options.adapter === undefined
				? new ConsoleLoggerAdapter()
				: new options.adapter();

		this.globalContext.set(ENVIRONMENT_KEY, getEnvironment());
	}

	public setLevel(level: LogLevel) {
		this.adapter.setLevel(level);
	}

	public addContext(key: string, value: any) {
		this.globalContext.set(key, value);
	}

	public error(error: unknown, context: Record<string, any> = {}) {
		const extendedContext = this.extend(context);

		this.adapter.error(error, extendedContext);
	}

	public warn(message: string, context: Record<string, any> = {}) {
		const extendedContext = this.extend(context);

		this.adapter.warn(message, extendedContext);
	}

	public info(
		message: string,
		context: Record<string, any> = {},
		tag?: string,
	) {
		const extendedContext = this.extend(context);

		this.adapter.info(message, extendedContext, tag);
	}

	private extend(context: Record<string, any>): Record<string, any> {
		if (this.config.showCallerLocation) {
			context.location = this.getCallerLocation();
		}

		const info = Object.fromEntries(this.globalContext.entries());

		Object.assign(context, info);

		return context;
	}

	private getCallerLocation(): string {
		const stack = new Error().stack?.split("\n");
		if (!stack) return "Unknown location";
		for (let i = 2; i < stack.length; i++) {
			if (!stack[i]?.includes("Logger") && !stack[i]?.includes("at info")) {
				return stack[i]?.trim().replace(/^at\s+/, "") ?? "Unknown location";
			}
		}
		return "Unknown location";
	}
}
